import { Style, width } from '@boba-cli/chapstick';
import { WindowSizeMsg } from '@boba-cli/tea';

// src/model.ts
var Height = 1;
var MAX_FIRST_COLUMN_WIDTH = 30;
function truncate(text, maxWidth) {
  if (width(text) <= maxWidth) {
    return text;
  }
  const ellipsis = "...";
  const availableWidth = maxWidth - width(ellipsis);
  if (availableWidth <= 0) {
    return ellipsis.slice(0, maxWidth);
  }
  let result = "";
  for (const char of text) {
    if (width(result + char) > availableWidth) {
      break;
    }
    result += char;
  }
  return result + ellipsis;
}
var StatusbarModel = class _StatusbarModel {
  constructor(state) {
    this.state = state;
  }
  /**
   * Create a new statusbar with column colors.
   * @param first - Color configuration for the first column
   * @param second - Color configuration for the second column
   * @param third - Color configuration for the third column
   * @param fourth - Color configuration for the fourth column
   * @returns A new StatusbarModel instance
   * @public
   */
  static new(first, second, third, fourth) {
    return new _StatusbarModel({
      width: 0,
      height: Height,
      firstColumn: "",
      secondColumn: "",
      thirdColumn: "",
      fourthColumn: "",
      firstColumnColors: first,
      secondColumnColors: second,
      thirdColumnColors: third,
      fourthColumnColors: fourth
    });
  }
  /**
   * Set the total width of the statusbar.
   * @param width - The total width in characters
   * @returns A new StatusbarModel with updated width
   * @public
   */
  setSize(width) {
    return new _StatusbarModel({
      ...this.state,
      width: Math.max(0, width)
    });
  }
  /**
   * Set text content for all columns.
   * @param first - Text for the first column
   * @param second - Text for the second column
   * @param third - Text for the third column
   * @param fourth - Text for the fourth column
   * @returns A new StatusbarModel with updated content
   * @public
   */
  setContent(first, second, third, fourth) {
    return new _StatusbarModel({
      ...this.state,
      firstColumn: first,
      secondColumn: second,
      thirdColumn: third,
      fourthColumn: fourth
    });
  }
  /**
   * Update colors for all columns.
   * @param first - Color configuration for the first column
   * @param second - Color configuration for the second column
   * @param third - Color configuration for the third column
   * @param fourth - Color configuration for the fourth column
   * @returns A new StatusbarModel with updated colors
   * @public
   */
  setColors(first, second, third, fourth) {
    return new _StatusbarModel({
      ...this.state,
      firstColumnColors: first,
      secondColumnColors: second,
      thirdColumnColors: third,
      fourthColumnColors: fourth
    });
  }
  /**
   * Handle messages (primarily window resize).
   * @param msg - The message to handle
   * @returns A tuple of the updated model and command
   * @public
   */
  update(msg) {
    if (msg instanceof WindowSizeMsg) {
      return [this.setSize(msg.width), null];
    }
    return [this, null];
  }
  /**
   * Render the statusbar as a string.
   * @returns The rendered statusbar
   * @public
   */
  view() {
    const { width: totalWidth } = this.state;
    if (totalWidth === 0) {
      return "";
    }
    const firstStyle = new Style().foreground(this.state.firstColumnColors.foreground).background(this.state.firstColumnColors.background).padding(0, 1).height(Height);
    const secondStyle = new Style().foreground(this.state.secondColumnColors.foreground).background(this.state.secondColumnColors.background).padding(0, 1).height(Height);
    const thirdStyle = new Style().foreground(this.state.thirdColumnColors.foreground).background(this.state.thirdColumnColors.background).padding(0, 1).height(Height).alignHorizontal("right");
    const fourthStyle = new Style().foreground(this.state.fourthColumnColors.foreground).background(this.state.fourthColumnColors.background).padding(0, 1).height(Height);
    const thirdRendered = thirdStyle.render(this.state.thirdColumn);
    const fourthRendered = fourthStyle.render(this.state.fourthColumn);
    const thirdWidth = width(thirdRendered);
    const fourthWidth = width(fourthRendered);
    const maxFirstContentWidth = Math.min(
      MAX_FIRST_COLUMN_WIDTH,
      Math.max(0, totalWidth - thirdWidth - fourthWidth - 4)
      // -4 for second column padding
    );
    const firstTruncated = truncate(
      this.state.firstColumn,
      maxFirstContentWidth
    );
    const firstRendered = firstStyle.render(firstTruncated);
    const firstWidth = width(firstRendered);
    const secondContentWidth = Math.max(
      0,
      totalWidth - firstWidth - thirdWidth - fourthWidth - 2
      // -2 for padding
    );
    const secondTruncated = truncate(
      this.state.secondColumn,
      secondContentWidth
    );
    const secondRendered = secondStyle.width(secondContentWidth + 2).render(secondTruncated);
    return [firstRendered, secondRendered, thirdRendered, fourthRendered].join(
      ""
    );
  }
};

export { Height, StatusbarModel };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map